import React from 'react';
import ReactDOM from 'react-dom/client';
import FeedBlogOverview from './FeedBlogOverview';
import FilterNavigationBlog from './FilterNavigationBlog';
import { FeedBlogOverviewProps, FilterNavigationBlogProps } from './types';

/**
 * Mounts the blog overview widgets rendered by
 * Seed.Backoffice/Views/Partials/grid/editors/widgets/BlogOverview/*.cshtml. Each mount point carries its
 * props as JSON in data-props. A mount point whose props do not parse is left as the server rendered it
 * and logged, rather than taking the other widgets on the page down with it.
 */

// No page holds anywhere near this many widgets; the cap bounds the loop.
const MAX_MOUNTS = 50;

function readProps<T>(element: Element): T | null {
    const raw = element.getAttribute('data-props');
    if (!raw) {
        console.error('Blog overview mount point has no data-props.', element);
        return null;
    }
    try {
        return JSON.parse(raw) as T;
    } catch (error) {
        console.error('Blog overview mount point has malformed data-props.', error);
        return null;
    }
}

function isFeedProps(value: FeedBlogOverviewProps | null): value is FeedBlogOverviewProps {
    return !!value && typeof value.rootId === 'number' && typeof value.categoryKey === 'string' && typeof value.pageSize === 'number'
        && (value.categories === null || value.categories === undefined || Array.isArray(value.categories));
}

function isFilterProps(value: FilterNavigationBlogProps | null): value is FilterNavigationBlogProps {
    return !!value && Array.isArray(value.filterButtons) && Array.isArray(value.columns);
}

export function mountBlogOverview(): void {
    const feeds = Array.from(document.querySelectorAll('[data-blog-overview-feed]')).slice(0, MAX_MOUNTS);
    feeds.forEach(element => {
        const props = readProps<FeedBlogOverviewProps>(element);
        if (!isFeedProps(props)) {
            return;
        }
        ReactDOM.createRoot(element).render(
            <React.StrictMode>
                <FeedBlogOverview {...props} />
            </React.StrictMode>
        );
    });

    const filters = Array.from(document.querySelectorAll('[data-blog-overview-filter]')).slice(0, MAX_MOUNTS);
    filters.forEach(element => {
        const props = readProps<FilterNavigationBlogProps>(element);
        if (!isFilterProps(props)) {
            return;
        }
        ReactDOM.createRoot(element).render(
            <React.StrictMode>
                <FilterNavigationBlog {...props} />
            </React.StrictMode>
        );
    });
}
